import type { MyMachines, WorkCenter } from '@/types/api'

/**
 * The machine list on the new-report form, ordered for the person holding it.
 *
 * A worker standing at a broken saw should find it in the first screenful.
 * Their own machines come first — the ones they are put on, then the ones
 * they reported from lately — and everything else follows grouped by plant,
 * in the order the administrators gave the list.
 */

/**
 * Folded for matching: lower case, no diacritics, and *đ* as *d* — which
 * Unicode does not decompose, so "Đurđevac" would otherwise never match
 * "djurdj" or "durd" typed on a phone keyboard without the letter.
 */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
}

export function matchesMachine(centre: WorkCenter, query: string): boolean {
  const needle = fold(query.trim())

  if (!needle) return true

  return [centre.name, centre.code, centre.plant?.name ?? '']
    .map(fold)
    .some((field) => field.includes(needle))
}

export interface MachineGroup {
  /** `mine` for the person's own machines, otherwise the plant's id. */
  key: string
  /** The plant's name; null for `mine`, which the screen titles itself. */
  plantName: string | null
  machines: WorkCenter[]
}

/**
 * The groups, in order, holding only what matches `query`.
 *
 * `mine` keeps the hints' order — assigned first, in their own order, then the
 * recent ones newest first — because that is the order of likelihood. A hint
 * for a machine not in `centres` (switched off, or in a plant this person no
 * longer sees) is dropped rather than shown as a blank row. Empty groups are
 * left out.
 */
export function machineGroups(
  centres: WorkCenter[],
  hints: MyMachines | undefined,
  query: string,
): MachineGroup[] {
  const byId = new Map(centres.map((centre) => [centre.id, centre]))
  const mineIds = [...new Set([...(hints?.assigned ?? []), ...(hints?.recent ?? [])])].filter(
    (id) => byId.has(id),
  )
  const mine = new Set(mineIds)

  const groups: MachineGroup[] = [
    {
      key: 'mine',
      plantName: null,
      machines: mineIds.map((id) => byId.get(id)!).filter((centre) => matchesMachine(centre, query)),
    },
  ]

  const plants = new Map<string, MachineGroup>()

  for (const centre of centres) {
    if (mine.has(centre.id) || !matchesMachine(centre, query)) continue

    const key = centre.plantId ?? centre.plant?.id ?? ''
    let group = plants.get(key)

    if (!group) {
      group = { key, plantName: centre.plant?.name ?? null, machines: [] }
      plants.set(key, group)
    }

    group.machines.push(centre)
  }

  return [...groups, ...plants.values()].filter((group) => group.machines.length > 0)
}

/**
 * The machine id a link or a scanned sticker opened the form with.
 *
 * `workCenterId` is the name everything else in the app uses; `radniCentar`
 * is what the sticker redirect (QrRedirect.tsx) and the scanner page have
 * always sent, and a bookmark of either still carries it.
 */
export function machineFromQuery(params: URLSearchParams): string {
  return params.get('workCenterId') ?? params.get('radniCentar') ?? ''
}
